import { TextArea } from '@myosh/odin-components';
import { debounce } from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../redux/hooks';
import {
  useGetBulkRecordAiAnalysisPromptQuery,
  useGetSingleRecordAiAnalysisPromptQuery,
} from '../../../redux/services/ai-api';
import { setAISpecificConfig } from '../../../redux/slices/form-builder-slice';

const FormBuilderAI = ({ formId }: { formId: number | undefined }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: bulkRecordAiAnalysisPrompt } = useGetBulkRecordAiAnalysisPromptQuery(
    { formId: formId! },
    {
      skip: !formId,
    }
  );

  const { data: singleRecordAiAnalysisPrompt } = useGetSingleRecordAiAnalysisPromptQuery(
    { formId: formId! },
    {
      skip: !formId,
    }
  );

  const debouncedUpdate = useCallback(
    debounce((value: string, name: string) => {
      dispatch(setAISpecificConfig({ [name]: value }));
    }, 400),
    [dispatch]
  );

  const handleFieldChange = useCallback(
    (value: string | undefined, name: string) => {
      if (value === undefined || value === null) return;
      debouncedUpdate(value, name);
    },
    [debouncedUpdate]
  );

  return (
    <details
      className="mb-4 h-fit w-full cursor-pointer rounded-md border border-gray-4 bg-mono-1 p-2 outline-2 outline-primary-2"
      open
      onClick={(e) => e.preventDefault()}
    >
      <summary className="mb-4 flex list-none items-center justify-between border-b border-solid border-gray-2 text-lg font-semibold">
        {String(t('record-ai-analysis-enable'))}
      </summary>

      <div className="space-y-2 p-2">
        <div style={{ resize: 'none' }} className="[&_textarea]:resize-none">
          <TextArea
            value={bulkRecordAiAnalysisPrompt?.result || ''}
            label={t('form-level-global-prompt')}
            name="bulkRecordAiAnalysisPrompt"
            onChange={(value) => handleFieldChange(value, 'bulkRecordAiAnalysisPrompt')}
            maxLength={20000}
            rows={10}
            showCharacterCounter
          />
        </div>
        <div style={{ resize: 'none' }} className="[&_textarea]:resize-none">
          <TextArea
            value={singleRecordAiAnalysisPrompt?.result || ''}
            label={t('single-record-ai-analysis-prompt', 'Single Record AI Analysis Prompt')}
            name="singleRecordAiAnalysisPrompt"
            onChange={(value) => handleFieldChange(value, 'singleRecordAiAnalysisPrompt')}
            maxLength={20000}
            rows={10}
            showCharacterCounter
          />
        </div>
      </div>
    </details>
  );
};

export default React.memo(FormBuilderAI);
